import { useMutation } from '@tanstack/react-query'
import { Coords } from 'ComponentLibrary/src/atoms/Map'
import { PlaceDetails } from 'ComponentLibrary/src/molecules/AddressSearch/types'
import { useGoogleAPI } from 'ComponentLibrary/src/molecules/AddressSearch/useGoogleAPI'

const FETCH_COORDINATES_KEY = ['asset', 'coordinates']

const findInAddressComponent = (results: google.maps.GeocoderResult[], component: string) => {
    if (results.length === 0) {
        return ''
    }
    for (const comp of results[0].address_components) {
        if (comp.types.includes(component)) {
            return comp.long_name
        }
    }
    return ''
}

const toPlaceDetails = (results: google.maps.GeocoderResult[]): PlaceDetails => {
    const { lat, lng } = results[0].geometry.location.toJSON()
    const coords = {
        latitude: lat,
        longitude: lng,
    }
    const state = findInAddressComponent(results, 'administrative_area_level_1')
    let county = findInAddressComponent(results, 'administrative_area_level_3')
    const city = findInAddressComponent(results, 'locality')
    const community = findInAddressComponent(results, 'sublocality_level_1')
    const street = findInAddressComponent(results, 'route')
    const streetNumber = findInAddressComponent(results, 'street_number')
    const zipcode = findInAddressComponent(results, 'postal_code')
    const country = findInAddressComponent(results, 'country')

    if (county === 'Kreisfreie Stadt Berlin') {
        county = 'Berlin'
    }
    if (county === 'Kreisfreie Stadt Hamburg') {
        county = 'Hamburg'
    }

    const formatted_address = `${street} ${streetNumber}, ${zipcode} ${city}`

    return {
        formatted_address,
        state,
        county,
        city,
        community,
        street,
        streetNumber,
        zipcode,
        coords,
        country,
    }
}

type UseGoogleGeocoderAPIProps = {
    apiKey: string
    language?: string
    onError?: (error: Error) => void
}

export const useGoogleGeocoderAPI = ({ apiKey, language, onError }: UseGoogleGeocoderAPIProps) => {
    const { geocoderService } = useGoogleAPI(apiKey, language)

    const {
        mutate: fetchPlaceDetails,
        data: placeDetails,
        isError,
        isPending,
    } = useMutation({
        mutationKey: FETCH_COORDINATES_KEY,
        mutationFn: async ({ address }: { address: string | null }) => {
            if (geocoderService === undefined) {
                throw Error('Make sure to only fetch when the google maps service is loaded!')
            }
            return toPlaceDetails((await geocoderService.geocode({ address })).results)
        },
        onError,
    })

    return {
        fetchPlaceDetails,
        placeDetails,
        isError,
        isPending,
        finishedInitialization: geocoderService !== undefined,
    }
}

type GeoCoordinates = {
    latitude: number
    longitude: number
}

const INITIAL_COORDS: GeoCoordinates = {
    latitude: 48.12499200216,
    longitude: 11.603711768,
}

export const coordinatesToGoogleLatLng = (newCoordinates: GeoCoordinates = INITIAL_COORDS): Coords => {
    return {
        lat: newCoordinates.latitude,
        lng: newCoordinates.longitude,
    }
}
